<template>
    <div :class="`o-addPromo --step-${activeStepIdx}`">
        <header class="o-addPromo__header">
            <step-progress-bar
                :steps="steps"
                @changeStep="changeStep($event)"
                :label="activeLabel"
                class="mb-5 mb-lg-0"
            >
                <the-btn
                    class="btn-secondary" 
                    @click="launchWt"
                >
                    Jak dodać promocję?
                </the-btn>
            </step-progress-bar>
        </header>
        <main>
            <div class="o-addPromo__headerButtons mt-5 mb-4 position-relative">
                <h3
                    class="text-center pt-2-0 d-none d-lg-block"
                    v-html="activeLabel"
                ></h3>
                <the-btn
                    v-if="activeStepIdx === 0"
                    @click.native="clearForm(activeStepIdx)"
                    :disabled="isClearFormStep1Disabled"
                    class="btn-outline-custom o-addPromo__clearFormBtn"
                    >Wyczyść formularz</the-btn
                >
            </div>
            <!-- STEP 1 -->
            <div class="card py-4 px-sm-4 px-2" v-if="activeStepIdx === 0">
                <div class="row" style="gap: 1.5rem 0">
                    <div class="col-lg-6 col-12">
                        <the-input
                            class="mb-0 text-primary"
                            :label="'Nazwa promocji'"
                            :name="'np-name'"
                            :text="form.step1.name.value"
                            :invalid="!!form.step1.name.invalid"
                            :invalid-feedback="form.step1.name.invalid"
                            :tooltip="
                                'Ta nazwa będzie wykorzystywana w aplikacji mobilnej dla konsumentów'
                            "
                            :maxlength="150"
                            @input="
                                form.step1.name.value = $event
                                    form.step1.name.invalid = ''
                            "
                        />
                    </div>
                    <div class="col-lg-3 col-12">
                        <date-input
                            class="mb-0"
                            :val="form.step1.start_at.value"
                            :placeholder="'Data startu promocji'"
                            :invalid="form.step1.start_at.invalid"
                            :with-time="true"
                            :id="'np-start_at'"
                            :lock-past-days="[$moment().format('YYYY-MM-DD')]"
                            :lock-future-days="startAtLockFutureDays"
                            @change="
                                form.step1.start_at.value = $event
                                form.step1.start_at.invalid = ''
                            "
                        />
                    </div>
                    <div class="col-lg-3 col-12">
                        <date-input
                            class="mb-0"
                            :val="form.step1.end_at.value"
                            :placeholder="'Data zakończenia promocji'"
                            :invalid="form.step1.end_at.invalid"
                            :with-time="true"
                            :id="'np-end_at'"
                            :lock-past-days="[
                                form.step1.start_at.value ||
                                    $moment().format('YYYY-MM-DD')
                            ]"
                            :lock-future-days="endAtLockFutureDays"
                            @change="
                                form.step1.end_at.value = $event
                                form.step1.end_at.invalid = ''
                            "
                        />
                    </div>
                    <div
                        class="col-lg-3 col-12 d-flex align-items-center"
                        style="height: 50px"
                    >
                        <ui-checkbox-input
                            name="np-loyalty"
                            :value="form.step1.loyalty.value"
                            @change="onLoyaltyChange($event)"
                            label="Tylko z aplikacją"
                        />
                    </div>
                    <div class="col-lg-3 col-12">
                        <the-dropdown
                            data-walk="wtnp-1-2"
                            class="mb-0"
                            text="Typ promocji"
                            :options="promoTypes"
                            :first-option="false"
                            :selected-option="form.step1.type.value"
                            :invalid="form.step1.type.invalid"
                            @onChange="onPromoTypeChange($event)"
                        ></the-dropdown>
                    </div>
                    <div class="col-lg-3 col-12">
                        <the-dropdown
                            data-walk="wtnp-1-2"
                            class="mb-0"
                            text="Komunikacja w aplikacji"
                            :options="
                                $store.getters.getNewPromoMarketingsForSelect
                            "
                            :first-option="false"
                            :selected-option="form.step1.marketing_id.value"
                            :invalid="form.step1.marketing_id.invalid"
                            @onChange="onMarketingIdChange($event)"
                        />
                    </div>
                    <div class="col-lg-3 col-12">
                        <the-input
                            class="mb-0"
                            :label="setUsageLimitLabel"
                            :name="'np-usage_limit_per_location'"
                            type="number"
                            :min="0"
                            :max="999"
                            :prevent-decimals="
                                form.step1.type.value !==
                                    'Promo::WeightPeriodicPriceReduction'
                            "
                            :text="form.step1.usage_limit_per_receipt.value"
                            :invalid="
                                !!form.step1.usage_limit_per_receipt.invalid
                            "
                            :invalid-feedback="
                                form.step1.usage_limit_per_receipt.invalid
                            "
                            @input="
                                form.step1.usage_limit_per_receipt.value = $event
                                form.step1.usage_limit_per_receipt.invalid = ''
                            "
                            @blur="validateUsageLimitValue"
                        />
                    </div>
                </div>
            </div>
            <!-- STEP 2 -->
            <div class="card py-4 px-sm-4 px-2" v-if="activeStepIdx === 1">
                <div class="row" style="gap: 1rem 0">
                    <merchants-list
                        v-if="merchants"
                        class="col-12 pb-3"
                        :select-merchants="selectMerchants"
                        :select-merchant="selectMerchant"
                        :selected-merchants="selectedMerchants"
                        :merchants="merchants"
                        :display-limits="true"
                    >
                        <template v-slot:infoBar
                            ><div
                                class="flex-grow-1 text-sm a-infoBar a-infoBar--warning mb-4"
                            >
                                Liczba aktywnych promocji własnych jest
                                ograniczona. Jeśli limit zostanie wykorzystany,
                                można usunąć trwającą promocję, aby założyć
                                nową.
                            </div></template
                        >
                        <div class="d-flex ml-auto gap-0-5">
                            <date-input
                                class="mb-0"
                                :val="form.step1.start_at.value"
                                :placeholder="'Data startu promocji'"
                                :invalid="form.step1.start_at.invalid"
                                :with-time="true"
                                :id="'np-start_at'"
                                :lock-past-days="[
                                    $moment().format('YYYY-MM-DD')
                                ]"
                                :lock-future-days="startAtLockFutureDays"
                                @change="
                                    form.step1.start_at.value = $event
                                    form.step1.start_at.invalid = ''
                                "
                            />
                            <date-input
                                class="mb-0"
                                :val="form.step1.end_at.value"
                                :placeholder="'Data zakończenia promocji'"
                                :invalid="form.step1.end_at.invalid"
                                :with-time="true"
                                :id="'np-end_at'"
                                :lock-past-days="[
                                    form.step1.start_at.value ||
                                        $moment().format('YYYY-MM-DD')
                                ]"
                                :lock-future-days="endAtLockFutureDays"
                                @change="
                                    form.step1.end_at.value = $event
                                    form.step1.end_at.invalid = ''
                                "
                            />
                        </div>
                    </merchants-list>
                    <div v-if="form.step2.merchants.invalid" class="col-12">
                        <div class="text-danger">
                            {{ form.step2.merchants.invalid }}
                        </div>
                    </div>
                </div>
            </div>
            <!-- STEP 3 -->
            <div style="gap: 1rem 0" v-if="activeStepIdx === 2">
                <!-- product_sets -->
                <template v-if="isMix">
                    <div
                        v-for="(set, setIdx) in form.step3.product_sets"
                        :key="set.id"
                    >
                        <h5 class="mb-3">ZESTAW {{ setIdx + 1 }}</h5>
                        <div class="row mb-4">
                            <div class="col-sm-6 col-12">
                                <the-input
                                    class="mb-0 no-arrows text-right"
                                    :label="'Liczba sztuk w zestawie'"
                                    :invalid="!!set.weight.invalid"
                                    :invalid-feedback="set.weight.invalid"
                                    :name="'np-set_weight'"
                                    :text="set.weight.value"
                                    :prevent-decimals="true"
                                    type="number"
                                    @input="
                                        set.weight.value = $event
                                        set.weight.invalid = ''
                                    "
                                />
                            </div>
                            <div class="col-sm-6 col-12 mt-3 mt-sm-0">
                                <the-btn
                                    v-if="selectedMerchants.length > 1"
                                    @click.native="openProductSetPrices(setIdx)"
                                    class="w-100"
                                    >Dostosuj promocyjną cenę w
                                    sklepach</the-btn
                                >
                                <money-input
                                    v-else
                                    class="mb-0"
                                    :label="'Łączna cena za zestaw'"
                                    :name="'np-set_price'"
                                    :price="+set.price.value[0].price"
                                    @onChange="
                                        set.price.value[0].price = $event
                                    "
                                />
                                <div
                                    v-if="
                                        set.price.invalid &&
                                            selectedMerchants.length > 1
                                    "
                                    class="text-danger invalid-feedback d-block"
                                >
                                    {{ set.price.invalid }}
                                </div>
                            </div>
                        </div>
                        <table-products
                            class="col-12 px-0"
                            :clear-form="clearForm"
                            :products="set.products.value"
                            :set-idx="setIdx"
                            :set="set"
                        />
                        <div v-if="form.step3.invalid" class="text-danger mt-3">
                            {{ form.step3.invalid }}
                        </div>
                        <div v-if="set.invalid" class="text-danger mt-3">
                            {{ set.invalid }}
                        </div>
                    </div>
                </template>
                <!-- products -->
                <template v-else>
                    <table-products
                        class="w-100"
                        :clear-form="clearForm"
                        :products="form.step3.products.value"
                    />

                    <alt-ean-disclaimer
                        v-if="$store.getters.isWeight(form.step1.type.value)"
                        class="mt-3"
                    />

                    <div v-if="form.step3.invalid" class="mt-2">
                        <div class="text-danger">
                            {{ form.step3.invalid }}
                        </div>
                    </div>
                </template>
            </div>
            <!-- STEP 4 -->
            <div class="card py-4 px-sm-4 px-2" v-if="activeStepIdx === 3">
                <div class="row align-items-center" style="gap: 1rem 0">
                    <div class="col-md-3 col-12 text-center">
                        <img
                            v-if="mainImageUrls"
                            class="img-fluid"
                            :src="mainImageUrls.small"
                        />
                    </div>
                    <div class="col-md-5 col-12">
                        <div>
                            Nazwa promocji:
                            <h3>{{ form.step1.name.value }}</h3>
                        </div>
                        <div>
                            Typ:
                            <h5>
                                {{
                                    $store.getters.getPromoTypeByValue(
                                        form.step1.type.value
                                    )
                                }}
                            </h5>
                        </div>
                        <div>
                            Czas trwania:
                            <h5>
                                <a-icon icon="Calendar" />
                                Od
                                {{
                                    $moment(form.step1.start_at.value).format(
                                        'DD-MM-YYYY'
                                    )
                                }}
                                do
                                {{
                                    $moment(form.step1.end_at.value).format(
                                        'DD-MM-YYYY'
                                    )
                                }}
                            </h5>
                        </div>
                    </div>
                    <div class="col-md-4 col-12">
                        <div>
                            {{ setUsageLimitLabel }}:
                            <h5>
                                {{
                                    form.step1.usage_limit_per_receipt.value &&
                                    +form.step1.usage_limit_per_receipt
                                        .value !== 0
                                        ? form.step1.usage_limit_per_receipt
                                              .value
                                        : '-'
                                }}
                            </h5>
                        </div>
                        <div>
                            Tylko z aplikacją:
                            <h5>
                                {{ form.step1.loyalty.value ? 'Tak' : 'Nie' }}
                            </h5>
                        </div>
                        <div>
                            Produktów w promocji:
                            <h5>
                                {{ selectedProducts.length }}
                            </h5>
                        </div>
                    </div>
                    <div class="col-12 mt-5 m-merchantProductPrices">
                        <h3 class="text-center mb-4">
                            {{
                                selectedMerchants.length
                                    | plurals(
                                        'Wybrany sklep',
                                        'Wybrane sklepy',
                                        undefined,
                                        null,
                                        null,
                                        true
                                    )
                            }}:
                        </h3>
                        <div
                            class="t-shops-row p-0"
                            :class="{
                                '--active':
                                    showProductsMerchantId === merchant.id
                            }"
                            v-for="merchant in selectedMerchants"
                            :key="`merchant-${merchant.id}`"
                        >
                            <div
                                class="d-flex justify-content-between w-100 cursor-pointer p-3"
                                @click="showMerchantProducts(merchant.id)"
                            >
                                <span>
                                    {{
                                        `${merchant.name},
                                    ${setShopAddress({
                                        street: merchant.street,
                                        number: merchant.number
                                    })},`
                                    }}
                                    <span class="nowrap">
                                        {{
                                            `${setShopAddress({
                                                postcode: merchant.postcode,
                                                city: merchant.city
                                            }).replace(',', '')}`
                                        }}
                                    </span>
                                </span>
                                <a-icon icon="Dropdown" />
                            </div>
                            <div
                                class="m-merchantProductPrices__tableWrapper w-100 pt-0 pb-3"
                                v-if="
                                    showProductsMerchantId === merchant.id &&
                                        isMix
                                "
                            >
                                <div
                                    class="card"
                                    v-for="(set, setIdxx) in form.step3
                                        .product_sets"
                                    :key="setIdxx"
                                >
                                    <div class="product-set__head px-2">
                                        <div class="product-set__info">
                                            <h2 class="typo">
                                                Zestaw produktów promocyjnych
                                                ({{
                                                    set.products.value.length
                                                        | plurals(
                                                            'produkt',
                                                            'produkty',
                                                            'produktów'
                                                        )
                                                }})
                                            </h2>
                                            <p>
                                                Konsument może wybrać
                                                <strong>{{
                                                    set.weight.value
                                                        | plurals(
                                                            'dowolną sztukę',
                                                            'dowolne sztuki',
                                                            'dowolnych sztuk'
                                                        )
                                                }}</strong>
                                                z poniższej puli
                                            </p>
                                        </div>
                                        <div class="form-group">
                                            <label class="form-label"
                                                >Cena zestawu</label
                                            ><span class="u-price">{{
                                                set.price.value.find(
                                                    price =>
                                                        price.merchant_id ===
                                                        merchant.id
                                                ).price | currency
                                            }}</span>
                                        </div>
                                    </div>
                                    <div
                                        class="c-table table-merchant-product-prices"
                                    >
                                        <div class="c-thead px-2">
                                            <div class="c-td is-1">Produkt</div>
                                            <div class="c-td is-3">
                                                Cena poza promocją
                                            </div>
                                        </div>
                                        <div class="c-tbody">
                                            <div
                                                v-for="product in set.products
                                                    .value"
                                                :key="product.id"
                                                class="c-tr px-2"
                                            >
                                                <div class="c-td is-1">
                                                    <strong>{{
                                                        product.name
                                                    }}</strong>
                                                    <p class="mb-0">
                                                        EAN: {{ product.ean }}
                                                    </p>
                                                </div>
                                                <div class="c-td is-3">
                                                    <span class="typo-label"
                                                        >Cena poza
                                                        promocją</span
                                                    >
                                                    {{
                                                        product.merchant_prices.find(
                                                            mP =>
                                                                mP.merchant_id ===
                                                                merchant.id
                                                        ).normal_price
                                                            | currency
                                                    }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="
                                    showProductsMerchantId === merchant.id &&
                                        !isMix
                                "
                                class="m-merchantProductPrices__tableWrapper w-100 pt-0 pb-3"
                            >
                                <div
                                    class="c-table table-merchant-product-prices"
                                >
                                    <div class="c-thead px-2">
                                        <div class="c-td is-1">Produkt</div>
                                        <div
                                            v-if="isTypeWeight"
                                            class="c-td is-2"
                                        >
                                            Kod lokalny
                                        </div>
                                        <div class="c-td is-2">
                                            Cena w promocji
                                        </div>
                                        <div class="c-td is-3">
                                            Cena poza promocją
                                        </div>
                                    </div>
                                    <div class="c-tbody">
                                        <div
                                            v-for="product in form.step3
                                                .products.value"
                                            :key="product.id"
                                            class="c-tr px-2"
                                        >
                                            <div class="c-td is-1">
                                                <strong>{{
                                                    product.name
                                                }}</strong>
                                                <p class="mb-0">
                                                    EAN: {{ product.ean }}
                                                </p>
                                            </div>
                                            <div
                                                v-if="isTypeWeight"
                                                class="c-td is-2"
                                            >
                                                <span class="typo-label"
                                                    >Kod lokalny</span
                                                >
                                                {{
                                                    product.merchant_prices.find(
                                                        mP =>
                                                            mP.merchant_id ===
                                                            merchant.id
                                                    ).alternative_ean
                                                        ? product.merchant_prices.find(
                                                              mP =>
                                                                  mP.merchant_id ===
                                                                  merchant.id
                                                          ).alternative_ean
                                                        : '---'
                                                }}
                                            </div>
                                            <div
                                                v-if="!isMix"
                                                class="c-td is-2"
                                            >
                                                <span class="typo-label"
                                                    >Cena w promocji</span
                                                >
                                                {{
                                                    product.merchant_prices.find(
                                                        mP =>
                                                            mP.merchant_id ===
                                                            merchant.id
                                                    ).promo_price
                                                        | currency(
                                                            form.step1.type
                                                                .value
                                                        )
                                                }}
                                            </div>
                                            <div class="c-td is-3">
                                                <span class="typo-label"
                                                    >Cena poza promocją</span
                                                >
                                                {{
                                                    product.merchant_prices.find(
                                                        mP =>
                                                            mP.merchant_id ===
                                                            merchant.id
                                                    ).normal_price
                                                        | currency(
                                                            form.step1.type
                                                                .value
                                                        )
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
        <footer
            class="o-addPromo__footer d-flex flex-wrap py-2 my-2 justify-content-end gap-0-5"
        >
            <div
                v-if="
                    activeStepIdx === 0 &&
                        limits &&
                        form.step1.start_at.value &&
                        form.step1.end_at.value
                "
                class="flex-grow-1 text-sm a-infoBar a-infoBar--warning"
            >
                We wskazanej dacie obowiązywania promocji można założyć promocje
                w
                {{
                    merchantsWithAvailableLimit.length
                        | plurals('sklepie', 'sklepach')
                }}
            </div>
            <the-btn
                v-if="activeStepIdx > 0"
                @click.native="changeStep(activeStepIdx - 1)"
                class="o-addPromo__btn--prev btn-outline-custom mr-auto"
                >Wstecz</the-btn
            >

            <v-popover
                v-if="activeStepIdx === 3"
                offset="8"
                class="o-addPromo__btn--clear"
            >
                <the-btn class="btn-danger">Odrzuć promocję</the-btn>

                <template slot="popover">
                    <popover-confirm
                        message="Czy na pewno chcesz usunąć wprowadzone dane?"
                        @confirmed="clearForm(activeStepIdx)"
                    />
                </template>
            </v-popover>
            <the-btn
                class="o-addPromo__btn--next"
                :class="[
                    {
                        disabled:
                            activeStepIdx === 0 &&
                            isEveryLocationAboutToClose &&
                            (isLatestClosingDateBeforePromoStart() ||
                                isLatestClosingDateBeforePromoEnd())
                    }
                ]"
                :id="
                    `add-promo-${
                        this.activeStepIdx === this.steps.length - 1
                            ? 'final'
                            : 'next'
                    }-step`
                "
                :loader="loader"
                :text="nextStepButtonText"
                @click.native="changeStep(activeStepIdx + 1)"
            />
        </footer>
    </div>
</template>

<script>
import StepProgressBar from '@/components/ui/StepProgressBar'
import UiCheckboxInput from '../../global/ui/UiCheckboxInput.vue'
import MerchantsList from '@/components/merchants/MerchantsList'
import TableProducts from '@/components/promotions/addNewPromo/TableProducts'
import AltEanDisclaimer from '@/components/promotions/altEans/AltEanDisclaimer'
import { addNewPromo } from '@/mixins/addNewPromo'
import { validateFormFields } from '@/mixins/validateFormFields'
import { tour } from '@/walkthroughs/newPromo.js'

export default {
    mixins: [addNewPromo, validateFormFields],
    data() {
        return {
            activeStepIdx: 0,
            showProductsMerchantId: null,
            initForm: null,
            initSteps: null,
            loader: false,
        }
    },
    components: {
        StepProgressBar,
        UiCheckboxInput,
        MerchantsList,
        TableProducts,
        AltEanDisclaimer
    },
    methods: {
        async onPromoTypeChange(event) {
            this.form.step1.type.value = event
            this.form.step1.type.invalid = ''
            this.setPriceObjects()
            this.form.step3.main_image_dict_product_id.value = null
            if (this.form.step1.usage_limit_per_receipt.value) {
                this.validateUsageLimitValue()
            }
            await this.fetchPromoMarketings()
            this.validatePromoMarketings()
        },
        async onLoyaltyChange(event) {
            this.form.step1.loyalty.value = event
            await this.fetchPromoMarketings()
            this.validatePromoMarketings()
        },
        onMarketingIdChange(event) {
            this.$set(this.form.step1.marketing_id, 'value', event)
            this.validatePromoMarketings()
        },
        showMerchantProducts(merchantId) {
            if (this.showProductsMerchantId === merchantId) {
                this.showProductsMerchantId = null
            } else {
                this.showProductsMerchantId = merchantId
            }
        },
        async acceptPromo() {
            if (this.$store.getters.getWalkthroughActive) {
                this.$store.dispatch('openModal', {
                    component: 'ModalAddPromoSummary',
                    args: {
                        onAnotherPromoAdd: () => {
                            this.$store.dispatch('toggleModal', false)
                        }
                    }
                })
                return
            }
            if (!this.validateFormDates()) return
            if (!this.validateMerchants()) return

            const { form } = this
            const altEansToSave = []
            const altEansToUpdate = []

            const pushAltEans = (errors, prices) => {
                const merchantId = prices.merchant_id
                const altEan = prices.alternative_ean

                if (altEan && errors?.length) {
                    const err = errors.find(er => {
                        return (
                            er.alternative_ean === altEan &&
                            er.merchant_id === merchantId
                        )
                    })
                    if (err) {
                        altEansToUpdate.push({
                            id: err.id,
                            alternative_ean: altEan
                        })
                    } else {
                        altEansToSave.push({
                            merchant_id: merchantId,
                            alternative_ean: altEan,
                            dict_product_id: prices.temp_id
                        })
                    }
                } else if (altEan) {
                    altEansToSave.push({
                        merchant_id: merchantId,
                        alternative_ean: altEan,
                        dict_product_id: prices.temp_id
                    })
                }
            }

            let subscriptions
            if (this.isMix) {
                const attributes = form.step3.product_sets.map((set, idx) => {
                    return set.price.value.map(merchant => {
                        return {
                            merchant_id: merchant.merchant_id,
                            price: +merchant.price,
                            temp_id: idx + 1
                        }
                    })
                })
                const mergedAttributes = [].concat.apply([], attributes)
                subscriptions = {
                    state: 'accepted',
                    product_set_subscriptions_attributes: mergedAttributes
                }
            } else {
                const attributes = this.selectedProducts.map(product => {
                    return product.merchant_prices.map(mPrice => {
                        pushAltEans(product.eanErrors, mPrice)

                        return {
                            alternative_ean: mPrice.alternative_ean,
                            merchant_id: mPrice.merchant_id,
                            price: +mPrice.promo_price,
                            normal_price: +mPrice.normal_price,
                            temp_id: mPrice.temp_id
                        }
                    })
                })
                const mergedAttributes = [].concat.apply([], attributes)
                subscriptions = {
                    state: 'accepted',
                    product_subscriptions_attributes: mergedAttributes
                }
            }

            let startAt = `${this.$moment(form.step1.start_at.value).format(
                'DD-MM-YYYY'
            )} `
            if (this.$moment().isSame(form.step1.start_at.value, 'day')) {
                startAt += this.$moment(form.step1.start_at.value).format(
                    'HH:mm:ss'
                )
            } else {
                startAt += '00:01:00'
            }

            const payload = {
                promotion: {
                    name: form.step1.name.value,
                    type: form.step1.type.value,
                    marketing_id:
                        form.step1.marketing_id.value !== ''
                            ? form.step1.marketing_id.value
                            : undefined,
                    person_applying: this.$store.getters.getUser.email,
                    start_at: startAt,
                    end_at: `${this.$moment(form.step1.end_at.value).format(
                        'DD-MM-YYYY'
                    )} 23:59:00`,
                    usage_limit_per_receipt:
                        form.step1.usage_limit_per_receipt.value &&
                        +form.step1.usage_limit_per_receipt.value !== 0
                            ? +form.step1.usage_limit_per_receipt.value
                            : undefined,
                    loyalty: form.step1.loyalty.value,
                    logo_dict_product_id:
                        form.step3.main_image_dict_product_id.value
                },
                // if not mix, add products key
                ...(this.isMix
                    ? {}
                    : {
                        products: this.selectedProducts.map(el => {
                            return {
                                dict_product_id: el.id,
                                max_price: Math.max(
                                    ...el.merchant_prices.map(
                                        el => +el.promo_price
                                    )
                                ),
                                temp_id: el.promo_dict_product_id
                            }
                        })
                    }),
                // is mix, add product_sets key
                ...(this.isMix
                    ? {
                        product_sets: this.form.step3.product_sets.map(
                            (el, idx) => {
                                return {
                                    type: 'ProductSet::Promotional',
                                    required_weight: +el.weight.value,
                                    max_price: Math.max(
                                        ...el.price.value.map(
                                            price => +price.price
                                        )
                                    ),
                                    product_set_dict_products_attributes: el.products.value.map(
                                        prod => {
                                            return {
                                                dict_product_id: prod.id,
                                                weight: 1
                                            }
                                        }
                                    ),
                                    temp_id: idx + 1
                                }
                            }
                        )
                    }
                    : {}),
                attachments: [
                    {
                        url:
                            this.selectedProducts.find(
                                el =>
                                    el.id ===
                                    this.form.step3.main_image_dict_product_id
                                        .value
                            ).image?.urls?.original ||
                            this.selectedProducts.find(
                                el =>
                                    el.id ===
                                    this.form.step3.main_image_dict_product_id
                                        .value
                            ).dict_product?.image?.urls?.original,
                        type: 'Attachment::Promo::Logo'
                    }
                ],
                merchants: this.selectedMerchants.map(el => el.id),
                subscriptions
            }

            if (this.loader) {
                return
            }

            try {
                this.loader = true

                if (altEansToSave.length) {
                    this.$store.$api.post('saveMerchantAlternativeEans', {
                        save: altEansToSave
                    })
                }

                const res = await this.$store.dispatch('createAndAcceptPromo', payload)

                if (res && res.error) {
                    throw new Error(error)
                }

                await this.fetchLimits()

                this.$store.dispatch('openModal', {
                    component: 'ModalAddPromoSummary',
                    args: {
                        onAnotherPromoAdd: () => {
                            this.$store.dispatch('toggleModal', false)
                        }
                    }
                })
                this.clearForm()
            } catch (err) {
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content:
                        err.data?.message ||
                        'Założenie promocji nie powiodło się. Spróbuj ponownie później.'
                })
            } finally {
                this.loader = false
            }
        },
        async changeStep(wantedIndex) {
            const isPermission = await this.isShopUserPermissions()
            if (isPermission) {
                const currentIdx = this.steps.findIndex(el => !!el.active)

                // step back is always available
                const isStepAvailable =
                    wantedIndex > currentIdx
                        ? this.isStepAvailable(wantedIndex)
                        : true

                // accept promo on last step
                if (wantedIndex > this.steps.length - 1) {
                    this.acceptPromo()
                    return
                }

                if (
                    isStepAvailable &&
                    !!this.steps[currentIdx] &&
                    !!this.steps[wantedIndex]
                ) {
                    this.steps[currentIdx].active = false
                    this.steps[wantedIndex].active = true
                    this.steps[wantedIndex].disabled = false
                    this.activeStepIdx = wantedIndex
                }

                // when step changed, scroll up
                const top = document.querySelector('.o-addPromo__header')
                const headerHeight = document.querySelector('.site-header')
                    .clientHeight
                this.$scrollTo(top, 100, {
                    offset: -headerHeight
                })
            }
        },
        selectMerchants(value) {
            if (value) {
                const locationsAllowedClosingDate = this.$store.getters.getLocationsWithAllowedClosingDate(
                    this.form.step1.end_at.value
                )
                const merchants = this.merchants.filter(el =>
                    locationsAllowedClosingDate
                        .map(loc => loc.id)
                        .includes(el.location_id)
                )

                if (this.limits) {
                    const limits = this.limits
                        .filter(
                            el =>
                                +el.active_promotions <
                                +el.retailer_promos_limit
                        )
                        .map(el => el.merchant_id)
                    const availableMerchants = merchants.filter(el =>
                        limits.includes(el.id)
                    )
                    this.form.step2.merchants.value = availableMerchants
                } else {
                    this.form.step2.merchants.value = merchants
                }
                this.form.step2.merchants.invalid = ''
            } else {
                this.form.step2.merchants.value = []
            }
        },
        selectMerchant(payload) {
            const { value, merchantId } = payload
            const merchant = this.merchants.find(el => el.id === merchantId)
            if (value) {
                this.form.step2.merchants.invalid = ''
                this.form.step2.merchants.value.push(merchant)
            } else {
                this.form.step2.merchants.value = this.form.step2.merchants.value.filter(
                    el => el.id !== merchantId
                )
            }
        },
        isStepAvailable(wantedIndex) {
            if (!this.validateFormDates()) return false
            switch (wantedIndex) {
            case 1: {
                const validateClosingDates = this
                    .isEveryLocationAboutToClose
                    ? !this.isLatestClosingDateBeforePromoStart() &&
                          !this.isLatestClosingDateBeforePromoEnd()
                    : true
                const validate =
                        this.validateFormFields(this.form.step1) &&
                        validateClosingDates
                if (!validate) this.disableNextSteps(wantedIndex)
                return validate
            }
            case 2: {
                const validate =
                        this.validateMerchants() &&
                        this.validateMerchantsClosingDates() &&
                        this.validateFormFields(this.form.step1) &&
                        this.validateFormFields(this.form.step2) &&
                        this.availableMerchantsIncludeSelected
                if (!validate) this.disableNextSteps(wantedIndex)

                return validate
            }
            case 3: {
                const step3 = { ...this.form.step3 }
                let validate = false
                if (this.isMix) {
                    delete step3.products
                    if (
                        step3.product_sets[0].products.value.length === 1 &&
                            !step3.main_image_dict_product_id.value
                    ) {
                        this.chooseMainImage(
                            step3.product_sets[0].products.value[0],
                            true
                        )
                    }
                    step3.product_sets.forEach(set => {
                        set.invalid = ''
                        validate = this.validateFormFields({
                            ...set,
                            main_image_dict_product_id: this.form.step3
                                .main_image_dict_product_id
                        })
                        if (!this.validateSetPrices(set)) {
                            validate = false
                        }
                        if (!validate) {
                            if (set.products.invalid) {
                                set.invalid = set.products.invalid
                            } else if (
                                this.form.step3.main_image_dict_product_id
                                    .invalid
                            ) {
                                this.form.step3.invalid = this.form.step3.main_image_dict_product_id.invalid
                            }
                        }
                        if (
                            validate &&
                                !this.validateProductPrices(
                                    set.products.value,
                                    this.isMix
                                )
                        ) {
                            validate = false
                            set.invalid =
                                    'Co najmniej jeden produkt nie ma określonej ceny'
                        }
                    })
                } else {
                    delete step3.product_sets
                    if (
                        step3.products.value.length === 1 &&
                            !step3.main_image_dict_product_id.value
                    ) {
                        this.chooseMainImage(step3.products.value[0], true)
                    }
                    validate = this.validateFormFields(step3)
                    if (!validate) {
                        if (this.form.step3.products.invalid) {
                            this.form.step3.invalid = this.form.step3.products.invalid
                        } else if (
                            this.form.step3.main_image_dict_product_id
                                .invalid
                        ) {
                            this.form.step3.invalid = this.form.step3.main_image_dict_product_id.invalid
                        }
                    }
                    if (
                        !this.validateProductPrices(
                            step3.products.value,
                            this.isMix
                        )
                    ) {
                        validate = false
                        this.form.step3.invalid =
                                'Co najmniej jeden produkt nie ma określonej ceny'
                    }
                    if (
                        validate &&
                            this.$store.getters.getNewPromoLocalCodesErrors
                                ?.length
                    ) {
                        validate = false
                        this.form.step3.invalid =
                                'Wprowadzone kody lokalne muszą być unikalne w sklepie'
                    }
                }
                validate =
                        validate &&
                        this.validateFormFields(this.form.step1) &&
                        this.validateFormFields(this.form.step2)

                if (!validate) this.disableNextSteps(wantedIndex)

                return validate
            }
            default:
                return false
            }
        },
        validateSetPrices(set) {
            let success = true
            set.price.value.forEach(price => {
                if (+price.price === 0) {
                    price.price = 1
                    setTimeout(() => {
                        price.price = 0
                    }, 0)
                    success = false
                    set.price.invalid = 'Należy dostosować cenę w sklepach'
                }
            })
            return success
        },
        validateProductPrices(products, isMix) {
            let success = true
            products.forEach(product => {
                product.merchant_prices.forEach(merchant => {
                    if (+merchant.promo_price === 0 && !isMix) {
                        merchant.promo_price = 1
                        setTimeout(() => {
                            merchant.promo_price = 0
                        }, 0)
                        success = false
                    }
                    // TEMP HIDDEN: PRICE_NORMAL
                    // if (+merchant.normal_price === 0) {
                    //     success = false
                    // }
                })
            })
            return success
        },
        async clearForm(stepIdx) {
            const initialForm = JSON.parse(this.initForm)
            switch (stepIdx) {
            case 0: {
                this.form.step1 = initialForm.step1
                this.fetchPromoMarketings()
                break
            }
            case 2: {
                if (this.isMix) {
                    this.form.step3.product_sets.forEach((set, idx) => {
                        set.products =
                                initialForm.step3.product_sets[idx].products
                    })
                } else {
                    this.form.step3.products = initialForm.step3.products
                }
                this.$store.dispatch('setRecentlySelectedProducts', [])
                break
            }
            default: {
                this.$store.dispatch('setAddNewPromoForm', initialForm)
                this.$store.dispatch(
                    'setAddNewPromoSteps',
                    JSON.parse(this.initSteps)
                )
                this.selectMerchants(true)
                this.changeStep(0)
                this.fetchPromoMarketings()
                break
            }
            }
        },
        fetchLimits() {
            if (
                this.$store.getters.getWalkthroughActive ||
                !this.form.step1.start_at.value ||
                !this.form.step1.end_at.value ||
                this.isLatestClosingDateBeforePromoStart() ||
                this.isLatestClosingDateBeforePromoEnd()
            ) {
                return
            }
            const format = 'YYYY-MM-DDT00:00:00'
            const today = this.$moment().format(format)
            const startAt = `${this.$moment(today)
                .subtract(10, 'years')
                .format(format)}Z,${this.$moment(
                this.form.step1.end_at.value
            ).format(format)}Z`
            const endAt = `${this.$moment(
                this.form.step1.start_at.value
            ).format(format)}Z,${this.$moment(today)
                .add(10, 'years')
                .format(format)}Z`

            return this.$store.dispatch('retailersLimitsFetch', {
                merchant_ids: this.selectedMerchants?.map(el => el.id),
                start_at: startAt,
                end_at: endAt
            })
        },
        setShopAddress(data) {
            return this.$store.getters.getShopAddress(data)
        },
        async onDatesChange() {
            if (this.$store.getters.getWalkthroughActive) return
            if (
                this.form.step1.start_at.value &&
                this.form.step1.end_at.value
            ) {
                this.$store.dispatch('setNetworkLimits', null)
                await this.fetchLimits()
                if (!this.steps[2].disabled) this.isStepAvailable(2)
            }
        },
        launchWt() {
            this.$store.dispatch('setActiveStepIdx', this.activeStepIdx)
            this.changeStep(0)
            this.$store.dispatch('setWalkthroughActive', true)
            setTimeout(() => {
                tour.start()
            }, 0)
        },
        async isShopUserPermissions() {
            let isPermissions = true
            if (this.$store.getters.isShopUser) {
                await this.$store.dispatch('userDataFetch').then(() => {
                    if (!this.$store.getters.isOwnPromoPermission) {
                        this.$router.push({ name: 'permission-denied' })
                        isPermissions = false
                    }
                })
            }
            return isPermissions
        },
        validateUsageLimitValue() {
            if (this.form.step1.usage_limit_per_receipt.value) {
                this.form.step1.usage_limit_per_receipt.value =
                    this.form.step1.type.value ===
                    'Promo::WeightPeriodicPriceReduction'
                        ? Number(this.form.step1.usage_limit_per_receipt.value)
                            .toFixed(3)
                            .toString()
                        : parseInt(
                            this.form.step1.usage_limit_per_receipt.value
                        ).toString()
            }
        },
        fetchPromoMarketings() {
            return this.$store.dispatch('loadNewPromoMarketings')
        }
    },
    computed: {
        newPromoActivationStatus() {
            return this.$store.getters.getNewPromoActivationStatus
        },
        merchants() {
            return this.$store.getters.getAvailableMerchants
        },
        countProducts() {
            if (this.isMix) {
                return this.form.step3.product_sets[0]?.products?.value?.length
            } else {
                return this.form.step3.products.value.length
            }
        },
        nextStepButtonText() {
            const merchantsWord =
                this.selectedMerchants.length > 1 ? 'sklepach' : 'sklepie'
            return this.activeStepIdx === this.steps.length - 1
                ? 'Utwórz i aktywuj w ' + merchantsWord
                : 'Dalej'
        },
        mainImageUrls() {
            const chosenProductId = this.form.step3.main_image_dict_product_id
                .value
            if (!chosenProductId) return
            if (this.isMix) {
                return this.form.step3.product_sets[0].products.value.find(
                    product => product.id === chosenProductId
                ).image.urls
            } else {
                return this.form.step3.products.value.find(
                    product => product.id === chosenProductId
                ).image.urls
            }
        },
        promoTypes() {
            return this.$store.getters.getNewPromoAllowedPromoTypesForSelect
        },
        activeLabel() {
            if (this.activeStepIdx === 2) {
                return (
                    this.steps[this.activeStepIdx].label +
                    ` <span>(${this.countProducts} / ${this.productsLimit})</span>`
                )
            } else {
                return this.steps[this.activeStepIdx].label
            }
        },
        setUsageLimitLabel() {
            return this.form.step1.type.value ===
                'Promo::WeightPeriodicPriceReduction'
                ? 'Limit kg na paragon'
                : 'Limit użyć na paragon'
        },
        changedProducts() {
            return this.$store.getters.getChangedProducts
        },
    },
    async created() {
        const isPermission = await this.isShopUserPermissions()

        if (!isPermission) {
            return
        }

        await this.fetchPromoMarketings()
        await this.fetchLimits()
        await this.getFormFromStorage()
        await this.getStepsFromStorage()
        await this.$store.dispatch('extendedDataFetch', {
            includes: 'locations'
        })
        this.activeStepIdx = this.steps.findIndex(el => el.active)
        this.changeStep(this.activeStepIdx)
        this.validateFormDates()
        if (!this.selectedMerchants.length) {
            this.selectMerchants(true)
        }

        if (this.changedProducts.length) {
            this.$store.dispatch('openModal', {
                component: 'AddPromoChangedProducts',
                args: {
                    products: this.changedProducts
                }
            })
        }
    },
    beforeCreate() {
        this.$store.$storage.getItem('initialState').then(res => {
            this.initForm = JSON.stringify(res.addNewPromo.form)
            this.initSteps = JSON.stringify(res.addNewPromo.steps)
        })
    },
    watch: {
        activeStepIdx: {
            handler(newValue, oldValue) {
                if (oldValue === 2) {
                    this.$store.dispatch('setRecentlySelectedProducts', [])
                }
            }
        },
        'form.step1.start_at.value'() {
            this.onDatesChange()
            this.form.step2.merchants.invalid = ''
        },
        'form.step1.end_at.value'() {
            this.onDatesChange()
            this.form.step2.merchants.invalid = ''
        }
    }
}
</script>
